import React from "react";
import Header from "./components/header";
import Footer from "./components/footer";
import { Helmet } from "react-helmet";
import { motion } from 'framer-motion';
import { useInView } from "react-intersection-observer";

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="iFLO Pro is the ultimate solution for eliminating HVAC water damage claims. Our patented technology prevents and dissolves bacteria causing clogs 24/7/365." />
        <meta name="keywords" content="HVAC, water damage, claims, prevention, bacteria, clogs, prevention, insurance, claims, reduction, iFLO Pro, HVAC maintenance, drain line cleaning" />
        <title>iFLO Pro | About Us - HVAC Water Damage Prevention Experts</title>
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="iFLO Pro | About Us - HVAC Water Damage Prevention Experts" />
        <meta property="og:description" content="iFLO Pro is the ultimate solution for eliminating HVAC water damage claims. Our patented technology prevents and dissolves bacteria causing clogs 24/7/365." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://iflopro.com/about" />
        <meta property="og:image" content="https://iflopro.com/about/proprietary.jpg" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="iFLO Pro | About Us - HVAC Water Damage Prevention Experts" />
        <meta name="twitter:description" content="iFLO Pro is the ultimate solution for eliminating HVAC water damage claims. Our patented technology prevents and dissolves bacteria causing clogs 24/7/365." />
        <meta name="twitter:image" content="https://iflopro.com/about/proprietary.jpg" />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="iFLO Pro" />
        <meta name="theme-color" content="#FBBF24" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://iflopro.com/about" />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "iFLO Pro",
            "url": "https://iflopro.com",
            "logo": "https://iflopro.com/logo.png",
            "description": "iFLO Pro is the ultimate solution for eliminating HVAC water damage claims. Our patented technology prevents and dissolves bacteria causing clogs 24/7/365.",
            "sameAs": [
              "https://www.facebook.com/iflopro",
              "https://www.linkedin.com/company/iflopro",
              "https://twitter.com/iflopro"
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "888-979-iFLO (4356)",
              "contactType": "customer service"
            }
          })}
        </script>
        
        {/* Product Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "iFLO Pro HVAC Water Damage Prevention System",
            "description": "iFLO Pro is the only cleaning solution that addresses the ENTIRE drain pan, breaking down zooglea bacteria at the source and guaranteeing no more drain line clogs.",
            "brand": {
              "@type": "Brand",
              "name": "iFLO Pro"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock"
            },
            "image": "https://iflopro.com/about/proprietary.jpg"
          })}
        </script>
      </Helmet>
      <Header />

      <main className="bg-white">
        {/* Hero Section */}
        <section className="relative w-full flex items-center justify-center">
          <div className="mx-auto w-full">
            <div className="relative">
              {/* Main Image Container */}
              <div className="relative w-full w-full mx-auto bg-white rounded-xl shadow-lg">
                <video
                  src="/installVideo.mp4"
                  alt="Pro Installation Demonstration"
                  className="w-full md:h-auto md:h-[950px] max-h-[850px] h-[50em] object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onContextMenu={(e) => e.preventDefault()}
                />
                
                {/* Text Overlay Card */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute md:left-20 md:right-0 left-4 right-4 bottom-10 bg-white shadow-xl p-6 max-w-md"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Leave No Room For Zooglea To Grow
                  </h2>
                  <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                    iFLO Pro is the only cleaning solution that addresses the ENTIRE drain pan, breaking down zooglea bacteria at the source and guaranteeing no more drain line clogs.
                  </p>
                  <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300">
                    I'm Interested
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Proprietary Formula Section */}
        <section className="bg-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left content - Product Images */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <img
                  src="/about/proprietary.jpg"
                  alt="iFLO Pro product bottles"
                />
              </motion.div>

              {/* Right content */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <h2 className="text-4xl font-bold text-gray-900">
                  PROPRIETARY FORMULA
                </h2>
                <div className="space-y-4">
                  <p className="text-lg text-gray-700">
                    The Pro formula is the most powerful iFLO solution on the market, breaking down zooglea bacteria in only 24 HOURS as opposed to the 72 hour time stamp of our consumer solutions.
                  </p>
                  <p className="text-lg text-gray-700">
                    Pros are given the choice between a 36oz or 72oz bottle of solution to go with their Pro Devices in order to allow for up to a year's worth of protection, keeping everyone aligned on when annual maintenance comes back around.
                  </p>
                  <p className="text-lg text-gray-700">
                    We believe our Pros should be trusted with the best tools we have to offer, making their services worth more to the customer in order to retain those relationships, and increase positive company revenue.
                  </p>
                </div>
                <div className="flex gap-4 pt-4">
                  <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
                    I'm Interested
                  </button>
                  <button onClick={() => window.location.href = '/about-formula'} className="bg-transparent border-2 border-yellow-400 text-black px-8 py-4 rounded-full font-semibold hover:bg-yellow-50 transition-all duration-300">
                    View the Formula →
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bottom Grid Sections */}
        <section className="bg-white py-20">
          <div className=" mx-auto ">
            <div className="grid md:grid-cols-2 gap-2">
              {/* Eco-Friendly Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden h-[600px] group"
              >
                <img
                  src="/about/ecofriendly.jpg"
                  alt="Eco-friendly demonstration"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-8">
                  <div className="text-center text-white">
                    <h3 className="text-3xl font-bold mb-4">ECO-FRIENDLY</h3>
                    <p className="text-lg mb-6">
                      iFLO is an enzyme-based formula, made safe for not only humans and pets, but also all of the metals and equipment in water air handlers.
                    </p>
                    <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
                      I'm Interested
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Certification Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative overflow-hidden h-[600px] group"
              >
                <img
                   src="/about/shaking.jpg"
                  alt="iFLO PRO Certification"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-8">
                  <div className="text-center text-white">
                    <h3 className="text-3xl font-bold mb-4">iFLO PRO CERTIFICATION</h3>
                    <p className="text-lg mb-6">
                      Becoming an iFLO Pro is simple, just like setting up an iFLO unit. Don't miss the opportunity to get ahead of the competition.
                    </p>
                    <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
                      Become a Pro
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* leave no room for zooglea to grow section */}
        <section className="bg-white md:py-0 py-12" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-8 md:gap-0 items-center"
            >
              {/* Left content */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6 order-2 md:order-1"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center md:text-left">Leave No Room For Zooglea To Grow</h2>
                <p className="text-base md:text-lg text-gray-700 text-center md:text-left">
                  iFLO Pro is the only cleaning solution that addresses the ENTIRE drain pan, breaking down zooglea bacteria at the source and guaranteeing no more drain line clogs.
                </p>
                <div className="flex justify-center md:justify-start">
                  <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-3 md:px-8 md:py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
                    I'm Interested
                  </button>
                </div>
              </motion.div>

              {/* Right content */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative bg-gray-200 order-1 md:order-2"
              >
                <img
                  src="/Insurance/cleaning.png"
                  alt="iFLO Pro Formula Process"
                  className="w-full h-[400px] md:h-[600px] object-contain"
                />
              </motion.div>
            </motion.div>
          </div>
        </section>

        
        

        {/* Protection Confirmation Section */}
        <section className="md:py-0 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-8 md:gap-0 items-center"
            >
              {/* Left content - Dashboard Images */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex items-center md:gap-0 gap-8 bg-gray-200"
              >
                <img src="/Insurance/panel.png" alt="Dashboard interface" className="w-full h-[400px] md:h-[600px] object-contain" />
              </motion.div>

              {/* Right content */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6 px-4"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center md:text-left">
                  Protection Confirmation 24/7/365
                </h2>
                <p className="text-base md:text-lg text-gray-600 text-center md:text-left">
                  Our real-time dashboard confirms your customers' units are protected, alongside access to the Pro app that provides regular updates on the status of their systems.
                </p>
                <div className="flex justify-center md:justify-start">
                  <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300">
                    I'm Interested
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Hardwired Float Switch Section */}
        <section className="bg-white md:py-0 py-12 mb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-8 md:gap-0 items-center"
            >
              {/* Left content */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-6 order-2 md:order-1"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center md:text-left">
                  Hardwired Float Switch Monitoring
                </h2>
                <p className="text-base md:text-lg text-gray-600 text-center md:text-left">
                  No need for a second transformer, iFLO connects to the existing panel with minimal power draw, along with battery back-up in case of local outages.
                </p>
                <div className="flex justify-center md:justify-start">
                  <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300">
                    I'm Interested
                  </button>
                </div>
              </motion.div>

              {/* Right content - Device Image */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex justify-end bg-gray-200 order-1 md:order-2"
              >
                <div className="w-full">
                  <img
                    src="/Insurance/hanger.png"
                    alt="iFLO device with float switch"
                    className="w-full h-[400px] md:h-[600px] object-contain"
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Numbers Section */}
        <section className="bg-white relative">
          <div className="w-full">
            <div className="grid md:grid-cols-2 items-stretch relative">
              {/* Map Image */}
              <div className="relative w-full h-[400px] md:h-[600px] bg-gray-900">
                <img
                  src="/Insurance/febmap.jpg"
                  alt="iFLO Installations Map"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>

              {/* Content - Overlapping the image */}
              <div className="bg-gray-50 p-12 flex items-center md:absolute md:right-0 md:top-1/2 md:transform md:-translate-y-1/2 md:w-[55%] md:shadow-xl md:z-10">
                <div className="max-w-lg">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    NUMBERS DON'T LIE
                  </h2>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    After only a year of iFLO, we've already amassed over 55,000 active units in customer homes with ZERO clogs caused by zooglea bacteria to report back! All around the country our customers are relieved and satisfied with the hassle-free help iFLO provides for both contractors and homeowners alike.
                  </p>
                  <div className="flex gap-4">
                    <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-6 py-3 rounded-full text-sm font-semibold transition-colors duration-300">
                      I'm Interested
                    </button>
                    {/*<button className="bg-white border-2 border-gray-300 hover:border-yellow-400 text-gray-900 px-6 py-3 rounded-full text-sm font-semibold transition-colors duration-300 flex items-center gap-2">
                      <span>Download Presentation</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </button>*/}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Certified Installer Section */}
        <section className="bg-white py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-12">
              <img
                src="/Insurance/probadge.png"
                alt="iFLO PRO Certified Installer Badge"
                className="w-56 h-56 mx-auto mb-12"
              />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-10 max-w-5xl mx-auto leading-tight">
              JOIN OUR CERTIFIED INSTALLERS AND EXPERIENCE THE iFLO PRO DIFFERENCE!
            </h2>
            <button onClick={() => window.location.href = '/signup'} className="bg-yellow-400 hover:bg-yellow-500 text-black px-10 py-4 rounded-full text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg">
              JOIN NOW
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default About;
